import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { protectedApi } from '../../services/api';
import axios from 'axios';

const API_URL = 'http://localhost:8000/api/v1';

// Async thunks
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async ({ search = '', skip = 0, limit = 12, category = undefined } = {}) => {
    try {
      const response = await axios.get(`${API_URL}/products`, {
        params: { search, skip, limit, category }
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      throw error.response?.data?.detail || 'Failed to fetch products';
    }
  }
);

export const fetchProductBySlug = createAsyncThunk(
  'products/fetchProductBySlug',
  async (slug) => {
    try {
      const response = await axios.get(`${API_URL}/products/by-slug/${slug}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || 'Failed to fetch product';
    }
  }
);

// Protected operations (require authentication)
export const createProduct = createAsyncThunk(
  'products/createProduct',
  async (productData) => {
    try {
      const response = await protectedApi.post('/products', productData);
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || 'Failed to create product';
    }
  }
);

export const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async ({ id, productData }) => {
    try {
      const response = await protectedApi.put(`/products/${id}`, productData);
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || 'Failed to update product';
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'products/deleteProduct',
  async (id) => {
    try {
      await protectedApi.delete(`/products/${id}`);
      return id;
    } catch (error) {
      throw error.response?.data?.detail || 'Failed to delete product';
    }
  }
);

const initialState = {
  items: [],
  currentProduct: null,
  status: 'idle',
  error: null,
  totalCount: 0
};

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Products
      .addCase(fetchProducts.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
        state.error = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
        state.items = [];
      })
      // Fetch Single Product
      .addCase(fetchProductBySlug.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchProductBySlug.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentProduct = action.payload;
        state.error = null;
      })
      .addCase(fetchProductBySlug.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // Create Product
      .addCase(createProduct.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items.push(action.payload);
        state.error = null;
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // Update Product
      .addCase(updateProduct.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.currentProduct?.id === action.payload.id) {
          state.currentProduct = action.payload;
        }
        state.error = null;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // Delete Product
      .addCase(deleteProduct.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = state.items.filter(item => item.id !== action.payload);
        if (state.currentProduct?.id === action.payload) {
          state.currentProduct = null;
        }
        state.error = null;
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  }
});

export const { clearCurrentProduct, clearError } = productsSlice.actions;
export default productsSlice.reducer;
